// Поэтажная шахматка ГПР (владелец 2026-09-15: «в визуализацию добавь поэтажную шахматку
// с датами окончания работ»). Четвёртый вид «Модели ГПР»: вместо таблицы задач — по каждому
// корпусу сетка «этаж × работа», в клетке — дата окончания по расчёту. Берёт те же строки,
// что таблица, поэтому фильтры панели и окна «Работа» действуют как обычно.
// Цвет клетки: закончено по факту, начато, окончание уже прошло, а работа не закончена.
// Клик по клетке открывает сведения о работе в нижней панели (владелец 2026-09-15; если
// задач в клетке несколько — первую), выбранная клетка подсвечена.
import { useMemo, type Ref } from 'react';
import type { Data } from './api';
import { fmt, slogi } from './util';

type Row = Data<'/api/projects/{pid}/tasks', 'get'>['rows'][number];
type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];

const DONE = ['СМР окончены', 'СМР приняты'];
const OBSHCHIJ = '—';           // строка задач без этажа (работа на корпус целиком)

type Kletka = { zadachi: Row[]; okonchanie: string | null; klass: string; podskazka: string };

export function ShahmatkaGPR({ rows, struct, meta, onOpen, tekId = null, boxRef, maxHeight }: {
  rows: Row[]; struct: Record<string, Floor[]>; meta: Record<string, Work>;
  onOpen: (row: Row) => void;
  /** задача, открытая в нижней панели, — её клетка подсвечена */
  tekId?: number | null;
  /** контейнер прокрутки: по нему таблица модели меряет место над панелью */
  boxRef?: Ref<HTMLDivElement>;
  /** высота сетки, когда внизу открыта панель */
  maxHeight?: string;
}) {
  const segodnya = new Date().toISOString().slice(0, 10);

  const korpusa = useMemo(() => {
    // порядок работ один на все корпуса: по этапу (как он встречается в модели), затем по началу
    const nachalo = new Map<string, string>();
    rows.forEach(r => {
      if (r.nachalo && (!nachalo.has(r.rabota) || r.nachalo < nachalo.get(r.rabota)!)) nachalo.set(r.rabota, r.nachalo);
    });
    const poRabote = (a: string, b: string) =>
      (nachalo.get(a) || '9999').localeCompare(nachalo.get(b) || '9999') || a.localeCompare(b, 'ru');

    const imena = [...new Set(rows.map(r => r.korpus || 'Объект'))];
    const poryadok = Object.keys(struct);
    imena.sort((a, b) => {
      const ia = poryadok.indexOf(a), ib = poryadok.indexOf(b);
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib) || a.localeCompare(b, 'ru');
    });

    return imena.map(k => {
      const svoi = rows.filter(r => (r.korpus || 'Объект') === k);
      const raboty = [...new Set(svoi.map(r => r.rabota))].sort(poRabote);
      // этажи сверху вниз, как в здании: по номеру из «Этажей», неизвестные — ниже
      const nomer = new Map((struct[k] || []).map(f => [String(f.etazh), f.num ?? -1e9]));
      // этажа нет в «Этажах» — берём число из названия («12» → 12), иначе в самый низ
      const nom = (e: string) => nomer.get(e) ?? (isNaN(parseFloat(e)) ? -1e9 : parseFloat(e));
      const etazhi = [...new Set(svoi.map(r => r.etazh || OBSHCHIJ))].sort((a, b) => {
        if (a === OBSHCHIJ) return 1;
        if (b === OBSHCHIJ) return -1;
        return nom(b) - nom(a) || b.localeCompare(a, 'ru', { numeric: true });
      });
      const kletki = new Map<string, Kletka>();
      svoi.forEach(r => {
        const kl = (r.etazh || OBSHCHIJ) + '¶' + r.rabota;
        const c = kletki.get(kl) || { zadachi: [], okonchanie: null, klass: '', podskazka: '' };
        c.zadachi.push(r);
        if (r.okonchanie && (!c.okonchanie || r.okonchanie > c.okonchanie)) c.okonchanie = r.okonchanie;
        kletki.set(kl, c);
      });
      kletki.forEach(c => {
        const vse = c.zadachi.every(z => DONE.includes(z.status || ''));
        const nachato = c.zadachi.some(z => z.status === 'СМР начаты' || DONE.includes(z.status || ''));
        c.klass = vse ? 'shg-ok' : (c.okonchanie && c.okonchanie < segodnya ? 'shg-pr' : (nachato ? 'shg-rab' : ''));
        const n = c.zadachi.map(z => z.nachalo).filter(Boolean).sort()[0];
        c.podskazka = c.zadachi[0].rabota + ', этаж ' + (c.zadachi[0].etazh || '—') + '\n'
          + 'начало ' + fmt(n) + ', окончание ' + fmt(c.okonchanie)
          + (c.zadachi.length > 1 ? '\nзадач в клетке: ' + c.zadachi.length : '')
          + (vse ? '\nзакончено по факту' : c.klass === 'shg-pr' ? '\nсрок прошёл, не закончено' : nachato ? '\nначато' : '')
          + '\nклик — сведения о работе в панели снизу';
      });
      return { k, raboty, etazhi, kletki };
    });
  }, [rows, struct, segodnya]);

  if (!rows.length) return <div className="pw-muted" style={{ padding: 12 }}>Задач по выбранным фильтрам нет.</div>;

  return (
    <div className="shg-wrap" ref={boxRef} style={maxHeight ? { maxHeight } : undefined}>
      <div className="shg-legenda pw-muted">
        <span className="shg-ok">закончено</span><span className="shg-rab">начато</span>
        <span className="shg-pr">срок прошёл</span><span>не начато</span>
        <span>· в клетке — дата окончания по расчёту</span>
      </div>
      {korpusa.map(({ k, raboty, etazhi, kletki }) => (
        <div key={k} className="shg-korpus">
          <div className="pw-section">{k}</div>
          <table className="shg">
            <thead>
              <tr>
                <th className="shg-r">Этаж</th>
                {raboty.map(w => (
                  <th key={w} className="shg-h" title={w + (meta[w]?.etap ? ' · ' + meta[w].etap : '')}>
                    <span>{slogi(w)}</span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {etazhi.map(e => (
                <tr key={e}>
                  <th className="shg-r">{e === OBSHCHIJ ? 'корпус' : e}</th>
                  {raboty.map(w => {
                    const c = kletki.get(e + '¶' + w);
                    if (!c) return <td key={w} className="shg-c shg-net" />;
                    return (
                      <td key={w} title={c.podskazka}
                        className={'shg-c ' + c.klass + (tekId != null && c.zadachi.some(z => z.id === tekId) ? ' shg-tek' : '')}
                        onClick={() => onOpen(c.zadachi[0])}>
                        {c.okonchanie ? fmt(c.okonchanie).slice(0, 5) + '.' + c.okonchanie.slice(2, 4) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
